import { useRoute, useRouter } from "vue-router";
import { watch, onMounted } from "vue";

/**
 * Utility to synchronize filter state with URL query parameters
 * @param {Object} options - Configuration options
 * @param {Object} options.filters - Reactive reference to filters object
 * @param {Array} options.excludeEmptyValues - Whether to exclude empty values (true by default)
 * @param {Array} options.excludeDefaults - Key-value pairs of default values to exclude from URL
 * @returns {Object} - Methods for interacting with URL query parameters
 */
export function useUrlQuerySync(options) {
  const route = useRoute();
  const router = useRouter();

  const {
    filters,
    excludeEmptyValues = true,
    excludeDefaults = [{ page: 1 }],
  } = options;

  /**
   * Updates URL query parameters based on current filters
   */
  const updateUrlFromFilters = () => {
    const query = {};

    // Convert filters to query parameters
    for (const [key, value] of Object.entries(filters.value)) {
      // Skip empty values if excludeEmptyValues is true
      if (
        excludeEmptyValues &&
        (value === "" ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0))
      ) {
        continue;
      }

      // Skip default values
      const shouldExclude = excludeDefaults.some((defaultItem) => {
        const defaultKey = Object.keys(defaultItem)[0];
        return key === defaultKey && value === defaultItem[defaultKey];
      });

      if (shouldExclude) {
        continue;
      }

      // Handle arrays by joining with commas
      if (Array.isArray(value)) {
        query[key] = value.join(",");
      } else {
        query[key] = String(value);
      }
    }

    // Replace URL without triggering navigation
    router.replace({
      query: { ...query },
    });
  };

  /**
   * Updates filters based on URL query parameters
   */
  const updateFiltersFromUrl = () => {
    const query = route.query;

    // Apply query parameters to filters
    for (const [key, value] of Object.entries(query)) {
      // Only update if the filter key exists
      if (key in filters.value) {
        // Check if the current filter value is an array to determine if we should parse as array
        const currentValue = filters.value[key];

        if (Array.isArray(currentValue)) {
          // Parse comma-separated string back to array
          if (typeof value === "string" && value.trim() !== "") {
            filters.value[key] = value
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item !== "");
          } else {
            filters.value[key] = [];
          }
        } else {
          // Convert values to appropriate types for non-array values
          if (value === "true") {
            filters.value[key] = true;
          } else if (value === "false") {
            filters.value[key] = false;
          } else if (!isNaN(Number(value)) && value !== "") {
            filters.value[key] = Number(value);
          } else {
            filters.value[key] = value;
          }
        }
      }
    }
  };

  // Watch for filter changes and update URL
  watch(filters, updateUrlFromFilters, { deep: true });

  // Initialize filters from URL on component mount
  onMounted(updateFiltersFromUrl);

  return {
    updateUrlFromFilters,
    updateFiltersFromUrl,
  };
}
